import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  ModalBody,
  ModalFooter,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';

import {
  BiConfig,
  BiConfigInput,
  biErrorMessage,
  biService,
} from '../../../../services/biService';
import { apllyToast } from '../../../Global/Toast2.0';
import { SecretInput } from '../../BI/SecretField';
import { ModalComponent } from '..';

export const BI_CONFIGS_KEY = 'bi-configs';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  config?: BiConfig;
}

type Field = keyof BiConfigInput;

// Valores usados pelo modo MasterUser, o único que o samasc-api implementa.
const DEFAULTS: BiConfigInput = {
  authenticationMode: 'MasterUser',
  authorityUri: 'https://login.microsoftonline.com/common/v2.0',
  scope: 'https://analysis.windows.net/powerbi/api',
  apiUrl: 'https://api.powerbi.com/',
  clientId: '',
  workspaceId: '',
  reportId: '',
  pbiUsername: '',
  pbiPassword: '',
  clientSecret: '',
  tenantId: '',
};

const REQUIRED: Field[] = [
  'pbiUsername',
  'pbiPassword',
  'authenticationMode',
  'authorityUri',
  'scope',
  'clientId',
];

const SECRET_FIELDS: Field[] = ['pbiPassword', 'clientSecret'];

const LABELS: Record<Field, string> = {
  pbiUsername: 'Usuário do Power BI (pbi_username)',
  pbiPassword: 'Senha (pbi_password)',
  clientId: 'Client ID',
  clientSecret: 'Client secret',
  tenantId: 'Tenant ID',
  authenticationMode: 'Modo de autenticação',
  authorityUri: 'Authority URI',
  scope: 'Scope',
  apiUrl: 'API URL',
  workspaceId: 'Workspace ID',
  reportId: 'Report ID',
};

const SECTIONS: { title: string; fields: Field[] }[] = [
  {
    title: 'Acesso ao Power BI',
    fields: ['pbiUsername', 'pbiPassword', 'clientId', 'clientSecret'],
  },
  {
    title: 'Autenticação',
    fields: ['authenticationMode', 'tenantId', 'authorityUri', 'scope'],
  },
  {
    title: 'Endereços',
    fields: ['apiUrl', 'workspaceId', 'reportId'],
  },
];

export function ModalConfigBI({ isOpen, onClose, config }: Props) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<BiConfigInput>(DEFAULTS);
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setErrors({});

    if (!config) {
      setForm(DEFAULTS);
      return;
    }

    const fromConfig = { ...DEFAULTS };

    (Object.keys(DEFAULTS) as Field[]).forEach(field => {
      fromConfig[field] = config[field] || '';
    });

    setForm(fromConfig);
  }, [isOpen, config]);

  const setField = (field: Field, value: string) =>
    setForm(current => ({ ...current, [field]: value }));

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    // Na edição, senha em branco mantém a atual (regra da API).
    const required = config
      ? REQUIRED.filter(field => field !== 'pbiPassword')
      : REQUIRED;
    const found: Partial<Record<Field, string>> = {};

    required.forEach(field => {
      if (!form[field].trim()) {
        found[field] = 'Campo obrigatório.';
      }
    });

    setErrors(found);

    if (Object.keys(found).length > 0) {
      return;
    }

    const payload = { ...DEFAULTS };

    // Senha e secret vão como digitados: espaço nas pontas pode fazer parte.
    (Object.keys(DEFAULTS) as Field[]).forEach(field => {
      payload[field] = SECRET_FIELDS.includes(field)
        ? form[field]
        : form[field].trim();
    });

    setSaving(true);
    try {
      if (config) {
        await biService.updateConfig(config.id, payload);
      } else {
        await biService.createConfig(payload);
      }

      await queryClient.invalidateQueries(BI_CONFIGS_KEY);
      apllyToast('success', 'Configuração salva.');
      onClose();
    } catch (err) {
      apllyToast(
        'error',
        biErrorMessage(err, 'Não foi possível salvar a configuração.'),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalComponent
      title={
        config ? 'Editar configuração de BI' : 'Adicionar nova config. de BI'
      }
      isOpen={isOpen}
      onClose={onClose}
      sizeSpecifyc="2xl"
      scrollBehavior="inside"
      closeOnOverlayClick={false}
    >
      <Box
        as="form"
        onSubmit={handleSubmit}
        autoComplete="off"
        display="flex"
        flexDirection="column"
        flex="1"
        minH="0"
      >
        <ModalBody>
          <Stack spacing="6">
            {SECTIONS.map(section => (
              <Box key={section.title}>
                <Text fontWeight="bold" color="gray.50" mb="3">
                  {section.title}
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4">
                  {section.fields.map(field => {
                    const id = `bi-config-${field}`;
                    const inputProps = {
                      id,
                      value: form[field],
                      onChange: (event: ChangeEvent<HTMLInputElement>) =>
                        setField(field, event.target.value),
                    };

                    return (
                      <FormControl key={field} isInvalid={!!errors[field]}>
                        <FormLabel htmlFor={id}>{LABELS[field]}</FormLabel>
                        {SECRET_FIELDS.includes(field) ? (
                          <SecretInput
                            {...inputProps}
                            autoComplete="new-password"
                          />
                        ) : (
                          <Input {...inputProps} autoComplete="off" />
                        )}
                        {SECRET_FIELDS.includes(field) && config && (
                          <FormHelperText>
                            Em branco, mantém a atual.
                          </FormHelperText>
                        )}
                        <FormErrorMessage>{errors[field]}</FormErrorMessage>
                      </FormControl>
                    );
                  })}
                </SimpleGrid>
              </Box>
            ))}
            <Text fontSize="xs" color="gray.500">
              O SAMASC autentica com usuário e senha (MasterUser). Client
              secret, tenant, workspace e report ID desta tela não são usados
              para gerar o token: cada relatório guarda o próprio workspace e
              report ID.
            </Text>
          </Stack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor="gray.600">
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancelar
          </Button>
          <Button colorScheme="blue" type="submit" isLoading={saving}>
            Salvar
          </Button>
        </ModalFooter>
      </Box>
    </ModalComponent>
  );
}
